import { supabase } from './supabase';
import type { Post } from './types';
import { compressImage } from './image-compress';

/**
 * Admin-only helpers and a couple of cross-profile counters that don't
 * belong in queries.ts (kept tight on scope per the task brief).
 *
 * RLS enforces role-gating for every write below: `allowed_emails` policies
 * in supabase/migrations/005_rls.sql only permit insert/update/delete for
 * role IN ('admin', 'developer'). The client call rides the session JWT
 * and is rejected server-side if the caller isn't authorised.
 */

// ---------- Allowed-emails CRUD ------------------------------------------

export interface AddAllowedEmailArgs {
  email: string;
  display_name?: string | null;
  is_founding_member?: boolean;
}

/**
 * Insert a new member into the whitelist. On their first sign-in the
 * `on_auth_user_created` trigger creates their profile. Comped (free) Club
 * access is granted ONLY when is_founding_member is true; everyone else
 * goes through the paywall.
 */
export async function addAllowedEmail(args: AddAllowedEmailArgs): Promise<void> {
  const email = args.email.trim().toLowerCase();
  if (!email) throw new Error('Email is required.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('That email looks invalid.');

  const { error } = await supabase.from('allowed_emails').insert({
    email,
    display_name: args.display_name?.trim() || null,
    is_founding_member: !!args.is_founding_member,
  });
  if (error) {
    // Friendlier error for dup-key conflicts.
    if (error.code === '23505' || /duplicate/i.test(error.message)) {
      throw new Error('That email is already on the list.');
    }
    throw error;
  }
}

/**
 * Set founding status on a whitelist row for someone who hasn't signed in yet
 * (no profile exists, so set_founding_member can't run). This updates the
 * `allowed_emails` seed; the value is copied onto their profile at first login.
 * For members who HAVE signed in, use setMemberFounding (members-admin-queries)
 * which is the live source of truth.
 */
export async function setAllowedEmailFounding(email: string, isFounding: boolean): Promise<void> {
  const clean = email.trim().toLowerCase();
  if (!clean) throw new Error('Email is required.');
  const { error } = await supabase
    .from('allowed_emails')
    .update({ is_founding_member: isFounding })
    .eq('email', clean);
  if (error) throw error;
}

/**
 * Remove a tester from the whitelist. NOTE: this only prevents future
 * first-login comping. It does NOT sign out a tester whose browser already
 * has a session, nor does it delete their `profiles` row. To fully revoke,
 * Cristo would also need to delete the auth user (service-role only) and
 * any related subscriptions/data - out of M1 scope.
 */
export async function removeAllowedEmail(email: string): Promise<void> {
  const clean = email.trim().toLowerCase();
  if (!clean) throw new Error('Email is required.');
  const { error } = await supabase.from('allowed_emails').delete().eq('email', clean);
  if (error) throw error;
}

// ---------- Comment-count helper (not in queries.ts per task brief) -------

/**
 * Count visible comments authored by a given user. `is_hidden = false` matches
 * what the feed/post view displays - hidden comments don't contribute.
 */
export async function countCommentsByAuthor(authorId: string, signal?: AbortSignal): Promise<number> {
  let q = supabase
    .from('comments')
    .select('id', { count: 'exact', head: true })
    .eq('author_id', authorId)
    .eq('is_hidden', false);
  if (signal) q = q.abortSignal(signal);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

// ---------- Contact inbox (admin only) -----------------------------------
// Backed by the legacy `feedback` table; UI surfaces it as "Contact".

export interface FeedbackRow {
  id: string;
  user_id: string | null;
  page_path: string | null;
  message: string;
  category: string | null;
  created_at: string;
  is_read: boolean;
  author?: {
    display_name: string | null;
    handle: string | null;
    avatar_url: string | null;
  } | null;
}

/**
 * Fetch the most recent contact submissions. RLS (see 005_rls.sql)
 * restricts SELECT on `public.feedback` to admin/developer roles.
 */
export async function fetchRecentFeedback(limit = 100, signal?: AbortSignal): Promise<FeedbackRow[]> {
  let q = supabase
    .from('feedback')
    .select(
      `id, user_id, page_path, message, metadata, created_at, is_read,
       author:profiles!feedback_user_id_fkey ( display_name, handle, avatar_url )`,
    )
    .order('created_at', { ascending: false })
    .limit(limit);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: r.id,
    user_id: r.user_id,
    page_path: r.page_path,
    message: r.message,
    category: typeof r.metadata?.category === 'string' ? r.metadata.category : null,
    created_at: r.created_at,
    is_read: Boolean(r.is_read),
    author: r.author ?? null,
  }));
}

/**
 * Flip is_read on a feedback row. Allowed by the 013 update policy
 * for admin/developer callers. The client call rides the session JWT.
 */
export async function markFeedbackRead(id: string, isRead: boolean): Promise<void> {
  const { error } = await supabase
    .from('feedback')
    .update({ is_read: isRead })
    .eq('id', id);
  if (error) throw error;
}

// ---------- Recent posts by author (for PublicProfile grid) --------------

export async function fetchRecentPostsByAuthor(authorId: string, limit = 6, signal?: AbortSignal): Promise<Post[]> {
  let q = supabase
    .from('posts_with_counts')
    .select(
      `id, slug, title, dek, published_at, created_at, post_type,
       media:media!posts_primary_media_fk ( id, thumbnail_url, kind )`,
    )
    .eq('author_id', authorId)
    .not('published_at', 'is', null)
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: r.id,
    slug: r.slug,
    title: r.title,
    dek: r.dek,
    body_md: null,
    post_type: r.post_type,
    author_id: authorId,
    primary_media_id: r.media?.id ?? null,
    external_url: null,
    published_at: r.published_at,
    created_at: r.created_at,
    media: r.media ?? null,
    drawers: [],
    like_count: 0,
    comment_count: 0,
  })) as Post[];
}

// ---------- Admin: banner presets (banner_presets + storage) --------------

export interface BannerPresetRow {
  id: string;
  label: string | null;
  image_url: string;
  thumb_url: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

const BANNER_PRESET_COLS = 'id, label, image_url, thumb_url, sort_order, is_active, created_at';

export async function fetchBannerPresetsAdmin(): Promise<BannerPresetRow[]> {
  const { data, error } = await supabase
    .from('banner_presets')
    .select(BANNER_PRESET_COLS)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as BannerPresetRow[];
}

export async function createBannerPreset(input: {
  label: string | null;
  image_url: string;
  thumb_url: string | null;
  sort_order: number;
  created_by?: string | null;
}): Promise<BannerPresetRow> {
  const { data, error } = await supabase
    .from('banner_presets')
    .insert({
      label: input.label,
      image_url: input.image_url,
      thumb_url: input.thumb_url,
      sort_order: input.sort_order,
      created_by: input.created_by ?? null,
    })
    .select(BANNER_PRESET_COLS)
    .single();
  if (error) throw error;
  return data as BannerPresetRow;
}

export async function updateBannerPreset(
  id: string,
  patch: Partial<Pick<BannerPresetRow, 'label' | 'is_active' | 'sort_order'>>,
): Promise<void> {
  const { error } = await supabase.from('banner_presets').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteBannerPreset(preset: {
  id: string;
  image_url: string;
  thumb_url: string | null;
}): Promise<void> {
  const paths = [
    storagePathFromPublicUrl(preset.image_url),
    preset.thumb_url ? storagePathFromPublicUrl(preset.thumb_url) : null,
  ].filter((x): x is string => Boolean(x));
  if (paths.length) {
    const { error: rmErr } = await supabase.storage.from('banners').remove(paths);
    if (rmErr) console.warn('[admin/banners] storage cleanup failed', rmErr);
  }
  const { error } = await supabase.from('banner_presets').delete().eq('id', preset.id);
  if (error) throw error;
}

export async function uploadBannerPresetImage(file: File): Promise<{ image_url: string; thumb_url: string }> {
  const full = await compressImage(file, { maxDim: 1600, quality: 0.8 });
  const thumb = await compressImage(file, { maxDim: 480, quality: 0.7 });
  const stamp = `${Date.now()}-${Math.round(Math.random() * 1e9).toString(36)}`;
  const fullPath = `presets/${stamp}.jpg`;
  const thumbPath = `presets/${stamp}-thumb.jpg`;
  const r1 = await supabase.storage.from('banners').upload(fullPath, full, { upsert: false, contentType: 'image/jpeg' });
  if (r1.error) throw r1.error;
  const r2 = await supabase.storage.from('banners').upload(thumbPath, thumb, { upsert: false, contentType: 'image/jpeg' });
  if (r2.error) throw r2.error;
  return {
    image_url: supabase.storage.from('banners').getPublicUrl(fullPath).data.publicUrl,
    thumb_url: supabase.storage.from('banners').getPublicUrl(thumbPath).data.publicUrl,
  };
}

function storagePathFromPublicUrl(url: string): string | null {
  const marker = '/object/public/banners/';
  const i = url.indexOf(marker);
  return i === -1 ? null : url.slice(i + marker.length);
}
